/**
 * In-race heads-up display for Weird Kart Racer.
 *
 * The HUD lives inside #hud-root (pointer-events:none) and is fully self
 * contained: it injects a guarded <style> block (all classes prefixed "hud-")
 * that reads the shared :root CSS variables. update() only writes text, widths
 * and opacities into cached elements — it never rebuilds the DOM — so it is safe
 * to call once per animation frame.
 *
 * Every size is in `rem`, i.e. a multiple of the root font size that src/style.css
 * derives from the viewport. The HUD therefore keeps the same proportions on a
 * 1366-wide laptop and a 4K monitor, and follows a window dragged between the
 * two without a reload.
 *
 * Beyond the readouts it carries two pieces of feel: a vignette that closes in
 * as the kart approaches its top speed, and speed streaks that fade up with it.
 * Both are pure CSS driven by a single 0..1 "strain" value.
 */
import type { CheckpointState } from './progress';

/** Everything the HUD needs to render one frame. */
export interface HudData {
  /** Kart ground speed in world units / second (converted to km/h for display). */
  speed: number;
  /** Current lap (1-based for display). */
  lap: number;
  /** Total laps in the race. */
  totalLaps: number;
  /** Current placing (1 = leader). */
  place: number;
  /** Number of racers. */
  totalPlayers: number;
  /** Elapsed race time in milliseconds. */
  time: number;
  /** Boost fill as a percentage, 0..100. */
  boost: number;
  /** True on frames where the boost is actually firing. */
  boosting: boolean;
  /** How hard the kart is being driven, 0..1 — drives the speed vignette. */
  strain: number;
}

/** Things the HUD hands back to the game. */
export interface HudCallbacks {
  onToggleMute(): void;
}

const STYLE_ID = 'hud-style';

const STYLE = `
.hud-root {
  position: absolute;
  inset: 0;
  pointer-events: none;
  font-family: "Segoe UI", system-ui, -apple-system, sans-serif;
  color: var(--text, #eef1ff);
  z-index: 20;
  display: none;
  font-size: 1rem;
}
.hud-root.hud-visible { display: block; }

/* Speed feel: a vignette that closes in, plus streaks raking past the edges. */
.hud-vignette {
  position: absolute;
  inset: 0;
  opacity: 0;
  background:
    radial-gradient(ellipse 78% 66% at 50% 52%, transparent 42%, rgba(4, 6, 14, 0.82) 100%);
  transition: opacity 0.18s linear;
}
.hud-streaks {
  position: absolute;
  inset: 0;
  opacity: 0;
  transition: opacity 0.22s linear;
  background:
    repeating-linear-gradient(90deg,
      rgba(255, 255, 255, 0.10) 0 2px,
      transparent 2px 26px);
  mask-image: radial-gradient(ellipse 62% 58% at 50% 50%, transparent 55%, #000 100%);
  -webkit-mask-image: radial-gradient(ellipse 62% 58% at 50% 50%, transparent 55%, #000 100%);
}

.hud-panel {
  position: absolute;
  background: var(--panel, rgba(18, 22, 38, 0.86));
  border: 1px solid var(--panel-border, rgba(255, 255, 255, 0.12));
  border-radius: 0.9rem;
  padding: 0.56rem 0.94rem;
  backdrop-filter: blur(10px);
  box-shadow: 0 0.5rem 1.6rem rgba(0, 0, 0, 0.42), inset 0 1px 0 rgba(255, 255, 255, 0.07);
  line-height: 1.15;
}

.hud-label {
  font-size: 0.69rem;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--muted, #9aa3c7);
  font-weight: 700;
}

.hud-lap { top: 1rem; left: 1rem; }
.hud-lap .hud-lap-value {
  font-size: 1.75rem;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
}
.hud-lap .hud-sep { color: var(--muted, #9aa3c7); font-weight: 600; }

/* Checkpoint pips: this lap's gates in crossing order, the finish line last.
   The track shows the same four states in the same four colours, so a glance at
   either answers "which one did I just miss?". */
.hud-cps {
  display: flex;
  align-items: center;
  gap: 0.2rem;
  margin-top: 0.42rem;
  height: 0.5rem;
}
.hud-cp {
  width: 0.6rem;
  height: 0.3rem;
  border-radius: 0.15rem;
  background: rgba(255, 255, 255, 0.18);
  transition: background 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease;
}
.hud-cp-passed { background: #53e08a; }
.hud-cp-next {
  background: var(--accent, #ffcf33);
  box-shadow: 0 0 0.5rem var(--accent, #ffcf33);
  transform: scaleY(1.6);
}
.hud-cp-missed {
  background: #ff3b3b;
  box-shadow: 0 0 0.5rem rgba(255, 59, 59, 0.85);
  animation: hud-blink 0.42s steps(1) infinite alternate;
}
/* The start/finish line closes the row out, and is worth more than a tick. */
.hud-cp-finish { width: 1.1rem; border-radius: 0.15rem; }
@keyframes hud-blink {
  from { opacity: 1; }
  to { opacity: 0.25; }
}

.hud-place { top: 1rem; right: calc(var(--mm-size, 11.9rem) + 2rem); text-align: right; }
.hud-place .hud-place-value {
  font-size: 1.75rem;
  font-weight: 800;
  color: var(--accent, #ffcf33);
}
.hud-place .hud-of {
  font-size: 1rem;
  color: var(--muted, #9aa3c7);
  font-weight: 600;
}

.hud-timer {
  top: 1rem;
  left: 50%;
  transform: translateX(-50%);
  text-align: center;
}
.hud-timer .hud-timer-value {
  font-size: 1.5rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

.hud-speedo {
  bottom: 1.25rem;
  left: 50%;
  transform: translateX(-50%);
  min-width: 15.6rem;
  text-align: center;
}
.hud-speed-row {
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 0.44rem;
}
.hud-speed-value {
  font-size: 2.75rem;
  font-weight: 900;
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.03em;
  text-shadow: 0 0 1.4rem rgba(255, 207, 51, 0);
  transition: text-shadow 0.2s ease;
}
.hud-speedo.hud-flatout .hud-speed-value {
  text-shadow: 0 0 1.4rem rgba(255, 207, 51, 0.55);
}
.hud-speed-unit {
  font-size: 0.94rem;
  color: var(--muted, #9aa3c7);
  font-weight: 700;
  letter-spacing: 0.08em;
}

.hud-boost { margin-top: 0.56rem; }
.hud-boost .hud-label { font-size: 0.63rem; margin-bottom: 0.31rem; display: block; }
.hud-boost-track {
  position: relative;
  height: 0.69rem;
  border-radius: 0.38rem;
  background: rgba(255, 255, 255, 0.09);
  overflow: hidden;
  box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.5);
}
.hud-boost-fill {
  position: absolute;
  inset: 0 auto 0 0;
  width: 0%;
  border-radius: 0.38rem;
  background: linear-gradient(90deg, var(--accent, #ffcf33), var(--accent-2, #ff5da2));
  box-shadow: 0 0 0.75rem var(--accent-2, #ff5da2);
  transition: width 0.08s linear;
}
.hud-speedo.hud-boosting .hud-boost-fill {
  animation: hud-pulse 0.32s ease-in-out infinite alternate;
}
@keyframes hud-pulse {
  from { filter: brightness(1); }
  to { filter: brightness(1.7); }
}

/* Mute toggle — the only clickable thing in the HUD layer. */
.hud-mute {
  position: absolute;
  bottom: 1.25rem;
  right: 1.25rem;
  pointer-events: auto;
  cursor: pointer;
  font: inherit;
  font-size: 1.13rem;
  line-height: 1;
  width: 2.75rem;
  height: 2.75rem;
  border-radius: 0.75rem;
  color: var(--text, #eef1ff);
  background: var(--panel, rgba(18, 22, 38, 0.86));
  border: 1px solid var(--panel-border, rgba(255, 255, 255, 0.12));
  backdrop-filter: blur(10px);
  transition: background 0.15s ease, transform 0.08s ease;
}
.hud-mute:hover { background: rgba(255, 255, 255, 0.14); }
.hud-mute:active { transform: translateY(1px); }

.hud-flash {
  position: absolute;
  top: 22%;
  left: 50%;
  transform: translate(-50%, 0);
  font-size: 2.13rem;
  font-weight: 900;
  text-align: center;
  color: var(--accent, #ffcf33);
  text-shadow: 0 0.19rem 0.9rem rgba(0, 0, 0, 0.7), 0 0 1.9rem rgba(255, 207, 51, 0.35);
  opacity: 0;
  transition: opacity 0.25s ease;
  padding: 0 1.13rem;
  max-width: 80%;
  pointer-events: none;
}
.hud-flash.hud-flash-on { opacity: 1; }
/* Bad news gets its own colour: at speed the banner is read as a shape long
   before it is read as words. */
.hud-flash.hud-flash-warn {
  color: #ff6b6b;
  text-shadow: 0 0.19rem 0.9rem rgba(0, 0, 0, 0.7), 0 0 1.9rem rgba(255, 59, 59, 0.45);
}

.hud-countdown {
  position: absolute;
  top: 42%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 8.1rem;
  font-weight: 900;
  color: var(--text, #eef1ff);
  text-shadow: 0 0.38rem 1.9rem rgba(0, 0, 0, 0.75), 0 0 3.75rem var(--accent, #ffcf33);
  display: none;
  text-align: center;
  line-height: 1;
}
.hud-countdown.hud-countdown-on {
  display: block;
  animation: hud-pop 0.45s cubic-bezier(0.2, 1.4, 0.4, 1) both;
}

@keyframes hud-pop {
  0% { transform: translate(-50%, -50%) scale(0.4); opacity: 0; }
  55% { transform: translate(-50%, -50%) scale(1.15); opacity: 1; }
  100% { transform: translate(-50%, -50%) scale(1); opacity: 1; }
}

/* ---------------------------------------------------------------------------
   Compact layout for phones and narrow windows.

   The desktop arrangement assumes room for a 190px minimap in the top-right
   corner with the placing panel beside it, and a wide speedo across the bottom
   centre. On a phone none of that is true, and the bottom corners belong to the
   two driving thumbs — so the placing moves under the lap counter, everything
   shrinks, and the mute button gets out of the way of the button stack.

   Keyed on a coarse pointer as well as width, so a phone in landscape (which
   can be wider than 860px) still gets the compact layout.
   --------------------------------------------------------------------------- */
@media (max-width: 860px), (pointer: coarse) {
  .hud-panel { padding: 0.38rem 0.69rem; border-radius: 0.75rem; }
  .hud-label { font-size: 0.56rem; letter-spacing: 0.12em; }

  .hud-lap {
    top: calc(0.63rem + env(safe-area-inset-top));
    left: calc(0.63rem + env(safe-area-inset-left));
  }
  .hud-lap .hud-lap-value { font-size: 1.25rem; }

  .hud-cps { gap: 0.13rem; margin-top: 0.31rem; height: 0.4rem; }
  .hud-cp { width: 0.4rem; height: 0.25rem; }
  .hud-cp-finish { width: 0.75rem; }

  /* Under the lap counter: the top-right corner is the minimap's. */
  .hud-place {
    top: calc(5.4rem + env(safe-area-inset-top));
    left: calc(0.63rem + env(safe-area-inset-left));
    right: auto;
    text-align: left;
  }
  .hud-place .hud-place-value { font-size: 1.25rem; }
  .hud-place .hud-of { font-size: 0.75rem; }

  .hud-timer { top: calc(0.63rem + env(safe-area-inset-top)); }
  .hud-timer .hud-timer-value { font-size: 1.06rem; }

  .hud-speedo {
    min-width: 0;
    bottom: calc(0.88rem + env(safe-area-inset-bottom));
    padding: 0.38rem 0.88rem;
  }
  .hud-speed-value { font-size: 1.75rem; }
  .hud-speed-unit { font-size: 0.69rem; }
  .hud-boost { margin-top: 0.31rem; }
  .hud-boost .hud-label { margin-bottom: 0.19rem; }
  .hud-boost-track { height: 0.5rem; }

  /* The bottom-right corner belongs to Boost and Drift now. */
  .hud-mute {
    width: 2.38rem;
    height: 2.38rem;
    font-size: 0.94rem;
    bottom: auto;
    top: calc(9.4rem + env(safe-area-inset-top));
    right: calc(0.75rem + env(safe-area-inset-right));
  }

  .hud-countdown { font-size: 5.25rem; }
  .hud-flash { font-size: 1.31rem; top: 27%; }
}
`;

/** Inject the HUD stylesheet exactly once per document. */
function ensureStyle(doc: Document): void {
  if (doc.getElementById(STYLE_ID)) return;
  const style = doc.createElement('style');
  style.id = STYLE_ID;
  style.textContent = STYLE;
  doc.head.appendChild(style);
}

/** Format a placing as an English ordinal: 1 -> "1st", 2 -> "2nd", 11 -> "11th". */
function ordinal(n: number): string {
  const abs = Math.abs(Math.trunc(n));
  const tens = abs % 100;
  const ones = abs % 10;
  let suffix = 'th';
  if (tens < 11 || tens > 13) {
    if (ones === 1) suffix = 'st';
    else if (ones === 2) suffix = 'nd';
    else if (ones === 3) suffix = 'rd';
  }
  return `${abs}${suffix}`;
}

/** Format milliseconds as m:ss.mmm (e.g. 83210 -> "1:23.210"). */
function formatTime(ms: number): string {
  const total = Math.max(0, Math.floor(ms));
  const minutes = Math.floor(total / 60000);
  const seconds = Math.floor((total % 60000) / 1000);
  const millis = total % 1000;
  const ss = seconds.toString().padStart(2, '0');
  const mmm = millis.toString().padStart(3, '0');
  return `${minutes}:${ss}.${mmm}`;
}

export class HUD {
  private readonly root: HTMLElement;
  private readonly el: HTMLDivElement;

  // Cached elements written by update() — never rebuilt per frame.
  private readonly speedoEl: HTMLDivElement;
  private readonly speedValueEl: HTMLSpanElement;
  private readonly boostFillEl: HTMLDivElement;
  private readonly lapValueEl: HTMLSpanElement;
  private readonly placeOrdinalEl: HTMLSpanElement;
  private readonly placeOfEl: HTMLSpanElement;
  private readonly timerValueEl: HTMLSpanElement;
  private readonly flashEl: HTMLDivElement;
  private readonly countdownEl: HTMLDivElement;
  private readonly vignetteEl: HTMLDivElement;
  private readonly streaksEl: HTMLDivElement;
  private readonly muteEl: HTMLButtonElement;
  private readonly cpsEl: HTMLDivElement;

  /** One element per checkpoint pip, rebuilt only when the count changes. */
  private cpPips: HTMLSpanElement[] = [];

  private flashTimer: number | null = null;

  constructor(root: HTMLElement, callbacks: HudCallbacks) {
    this.root = root;
    const doc = root.ownerDocument;
    ensureStyle(doc);

    const div = doc.createElement('div');
    div.className = 'hud-root';
    div.innerHTML = `
      <div class="hud-vignette" data-hud="vignette"></div>
      <div class="hud-streaks" data-hud="streaks"></div>
      <div class="hud-panel hud-lap">
        <div class="hud-label">Lap</div>
        <div class="hud-lap-value"><span data-hud="lap">1<span class="hud-sep"> / </span>3</span></div>
        <div class="hud-cps" data-hud="cps"></div>
      </div>
      <div class="hud-panel hud-timer">
        <div class="hud-label">Time</div>
        <div class="hud-timer-value" data-hud="timer">0:00.000</div>
      </div>
      <div class="hud-panel hud-place">
        <div class="hud-label">Place</div>
        <div class="hud-place-value"><span data-hud="place-ord">1st</span><span class="hud-of" data-hud="place-of"> / 1</span></div>
      </div>
      <div class="hud-panel hud-speedo" data-hud="speedo">
        <div class="hud-speed-row">
          <span class="hud-speed-value" data-hud="speed">0</span>
          <span class="hud-speed-unit">km/h</span>
        </div>
        <div class="hud-boost">
          <span class="hud-label">Boost</span>
          <div class="hud-boost-track"><div class="hud-boost-fill" data-hud="boost"></div></div>
        </div>
      </div>
      <button class="hud-mute" type="button" data-hud="mute" title="Mute (M)" aria-label="Mute">🔊</button>
      <div class="hud-flash" data-hud="flash"></div>
      <div class="hud-countdown" data-hud="countdown"></div>
    `;

    const q = <T extends HTMLElement>(name: string): T => {
      const found = div.querySelector<T>(`[data-hud="${name}"]`);
      if (!found) throw new Error(`HUD element missing: ${name}`);
      return found;
    };

    this.lapValueEl = q<HTMLSpanElement>('lap');
    this.timerValueEl = q<HTMLSpanElement>('timer');
    this.placeOrdinalEl = q<HTMLSpanElement>('place-ord');
    this.placeOfEl = q<HTMLSpanElement>('place-of');
    this.speedoEl = q<HTMLDivElement>('speedo');
    this.speedValueEl = q<HTMLSpanElement>('speed');
    this.boostFillEl = q<HTMLDivElement>('boost');
    this.flashEl = q<HTMLDivElement>('flash');
    this.countdownEl = q<HTMLDivElement>('countdown');
    this.vignetteEl = q<HTMLDivElement>('vignette');
    this.streaksEl = q<HTMLDivElement>('streaks');
    this.muteEl = q<HTMLButtonElement>('mute');
    this.cpsEl = q<HTMLDivElement>('cps');

    this.muteEl.addEventListener('click', () => callbacks.onToggleMute());

    root.appendChild(div);
    this.el = div;
  }

  /** Reveal the HUD. */
  show(): void {
    this.el.classList.add('hud-visible');
  }

  /** Hide the HUD (does not clear cached values). */
  hide(): void {
    this.el.classList.remove('hud-visible');
  }

  /** Reflect the audio mute state on the toggle button. */
  setMuted(muted: boolean): void {
    this.muteEl.textContent = muted ? '🔇' : '🔊';
    this.muteEl.title = muted ? 'Unmute (M)' : 'Mute (M)';
    this.muteEl.setAttribute('aria-label', muted ? 'Unmute' : 'Mute');
  }

  /** Write one frame of data into the cached elements. */
  update(data: HudData): void {
    const kmh = Math.round(data.speed * 3.6);
    this.speedValueEl.textContent = String(kmh);

    const boostPct = Math.max(0, Math.min(100, data.boost));
    this.boostFillEl.style.width = `${boostPct}%`;
    this.speedoEl.classList.toggle('hud-boosting', data.boosting);
    this.speedoEl.classList.toggle('hud-flatout', data.strain > 0.75);

    this.lapValueEl.innerHTML =
      `${data.lap}<span class="hud-sep"> / </span>${data.totalLaps}`;

    this.placeOrdinalEl.textContent = ordinal(data.place);
    this.placeOfEl.textContent = ` / ${data.totalPlayers}`;

    this.timerValueEl.textContent = formatTime(data.time);

    const strain = Math.max(0, Math.min(1, data.strain));
    this.vignetteEl.style.opacity = (strain * 0.85).toFixed(3);
    this.streaksEl.style.opacity = (Math.max(0, strain - 0.35) * 0.7).toFixed(3);
  }

  /**
   * Show this lap's checkpoints as a row of pips, in the order they are crossed
   * — so the LAST entry is the start/finish line, and gets drawn as such.
   *
   * The states match the gates on track and the ticks on the minimap: a red pip
   * and a red gate are the same missed checkpoint.
   */
  setCheckpoints(states: readonly CheckpointState[]): void {
    if (states.length !== this.cpPips.length) {
      const doc = this.root.ownerDocument;
      this.cpsEl.textContent = '';
      this.cpPips = states.map((_, i) => {
        const pip = doc.createElement('span');
        pip.className = i === states.length - 1 ? 'hud-cp hud-cp-finish' : 'hud-cp';
        this.cpsEl.appendChild(pip);
        return pip;
      });
    }
    for (let i = 0; i < states.length; i++) {
      const base = i === states.length - 1 ? 'hud-cp hud-cp-finish' : 'hud-cp';
      const state = states[i];
      this.cpPips[i].className = state === 'ahead' ? base : `${base} hud-cp-${state}`;
    }
  }

  /**
   * Show a transient banner near the top-center that fades after `ms`.
   * `warn` colours it red, for the things the driver has to react to.
   */
  flashMessage(text: string, ms = 1400, warn = false): void {
    if (this.flashTimer !== null) {
      this.root.ownerDocument.defaultView?.clearTimeout(this.flashTimer);
      this.flashTimer = null;
    }
    this.flashEl.textContent = text;
    this.flashEl.classList.toggle('hud-flash-warn', warn);
    // Force a reflow so re-triggering the same message restarts the transition.
    void this.flashEl.offsetWidth;
    this.flashEl.classList.add('hud-flash-on');

    const view = this.root.ownerDocument.defaultView;
    const setTimeoutFn = view ? view.setTimeout.bind(view) : setTimeout;
    this.flashTimer = setTimeoutFn(() => {
      this.flashEl.classList.remove('hud-flash-on');
      this.flashTimer = null;
    }, Math.max(0, ms)) as unknown as number;
  }

  /** Show a big centered countdown word ("3","2","1","GO!"); null clears it. */
  setCountdown(text: string | null): void {
    if (text === null) {
      this.countdownEl.classList.remove('hud-countdown-on');
      this.countdownEl.textContent = '';
      return;
    }
    // Retrigger the pop animation even when the text is unchanged.
    this.countdownEl.classList.remove('hud-countdown-on');
    this.countdownEl.textContent = text;
    void this.countdownEl.offsetWidth;
    this.countdownEl.classList.add('hud-countdown-on');
  }
}
